/**
 * CreateCampaignDialog - Wizard de criação de campanhas com design moderno
 * 
 * Layout de 2 colunas:
 * - Sidebar esquerda: navegação de steps + resumo em tempo real
 * - Conteúdo direita: formulário do step atual com scroll
 * 
 * @author Sistema de Campanhas
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useBlacklist } from '@/hooks/useBlacklist';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  Settings,
  Inbox,
  Users,
  MessageSquare,
  Shield,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Megaphone,
  Search,
  Clock,
  Zap,
  FileText,
  UserPlus,
  X,
  Phone,
  Mail,
  HelpCircle,
  Filter,
  AlertTriangle,
} from 'lucide-react';
import { CampaignOficialHelpDialog } from './CampaignOficialHelpDialog';
import { CampaignMessageBuilder } from './CampaignMessageBuilder';
import { CampaignTemplateSelector } from './CampaignTemplateSelector';
import { MessageGroupManager } from './MessageGroupManager';
import { CampaignActionBuilder } from './CampaignActionBuilder';
import { MessageItem, TemplateParams, CampaignMessageItemRecord, MessageGroup, MAX_CAMPAIGN_RECIPIENTS } from './types';
import { CampaignActionItem, CampaignActionRecord } from './campaign-actions-types';
import { normalizeBrazilianPhone, createNormalizedPhoneSet } from '@/lib/phone-utils';
import { createLogger } from '@/lib/logger';
import { SegmentBuilder } from './segment/SegmentBuilder';
import { CampaignSegment, SegmentPreviewContact, createEmptySegment } from '@/types/campaign-segments';
import { useAuth } from '@/hooks/useAuth';
import type { LucideIcon } from 'lucide-react';
import type { Database, Json } from '@/integrations/supabase/types';
// Badge compartilhado de visibilidade (mesmo padrão da tela de Listas)
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

const campaignLogger = createLogger('CreateCampaign');

// Tipo do channel_type do banco
type ChannelType = Database['public']['Enums']['channel_type'];

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface CreateCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  /**
   * Lista de campanha pré-selecionada (opcional).
   * Usado quando o wizard é aberto a partir da tela de Importação de Listas,
   * já posicionando a origem de destinatários em "Lista".
   */
  initialListId?: string | null;
}

interface InboxType {
  id: string;
  name: string;
  identifier: string;
  status: string;
  channel_type: string;
  api_provider?: {
    display_name: string;
    slug: string;
  };
}

interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
}

interface ManualRecipient {
  phone: string;
  name: string;
}

interface CampaignList {
  id: string;
  name: string;
  total_rows: number;
  variable_columns: string[];
  status: string;
  created_at: string;
  // Visibilidade configurada da lista (retornada pela RPC get_campaign_lists)
  visibility: EntityVisibility | null;
}

interface ListItem {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
  variables: Record<string, string> | null;
}

interface StepConfig {
  id: number;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  description: string;
}

// ============================================================================
// CONFIGURAÇÃO DOS STEPS
// ============================================================================

const STEPS: StepConfig[] = [
  { 
    id: 1, 
    label: 'Configuração', 
    shortLabel: 'Config',
    icon: Settings,
    description: 'Nome, descrição e tipo de canal da campanha'
  },
  { 
    id: 2, 
    label: 'Caixas de Entrada', 
    shortLabel: 'Inboxes',
    icon: Inbox,
    description: 'Selecione as caixas de entrada para envio'
  },
  { 
    id: 3, 
    label: 'Destinatários', 
    shortLabel: 'Destinos',
    icon: Users,
    description: 'Escolha os contatos que receberão a campanha'
  },
  { 
    id: 4, 
    label: 'Mensagem', 
    shortLabel: 'Mensagem',
    icon: MessageSquare,
    description: 'Configure o conteúdo da mensagem'
  },
  { 
    id: 5, 
    label: 'Delay', 
    shortLabel: 'Delay',
    icon: Clock,
    description: 'Configurações de delay e personalização'
  },
  { 
    id: 6, 
    label: 'Ações', 
    shortLabel: 'Ações',
    icon: Zap,
    description: 'Ações automáticas após cada disparo'
  },
  { 
    id: 7, 
    label: 'Agendamento', 
    shortLabel: 'Agendar',
    icon: CalendarDays,
    description: 'Escolha quando enviar a campanha'
  },
];

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

// Export nomeado para compatibilidade
export { CreateCampaignDialog };

function CreateCampaignDialog({
  open,
  onOpenChange,
  tenantId,
  initialListId = null,
}: CreateCampaignDialogProps) {
  const queryClient = useQueryClient();
  
  // Hook para verificar blacklist LGPD
  const { checkBlacklist } = useBlacklist();
  
  // Verificar se CRM está habilitado para o tenant
  const { currentTenant, user } = useAuth();
  const crmEnabled = currentTenant?.crm_enabled ?? false;
  
  // ---------------------------------------------------------------------------
  // Estados do Wizard
  // ---------------------------------------------------------------------------
  const [step, setStep] = useState(1);
  const totalSteps = STEPS.length;
  
  // Step 1 - Configuração básica
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [channelType, setChannelType] = useState<'whatsapp' | 'whatsapp_official'>('whatsapp');
  
  // Step 2 - Inboxes
  const [selectedInboxes, setSelectedInboxes] = useState<string[]>([]);
  
  // Step 3 - Destinatários
  // Default = 'list' (Lista CSV é a primeira aba na nova ordem visual)
  const [recipientSource, setRecipientSource] = useState<'contacts' | 'list' | 'manual' | 'segment'>('list');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [manualRecipients, setManualRecipients] = useState<ManualRecipient[]>([]);
  const [manualPhone, setManualPhone] = useState('');
  const [manualName, setManualName] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  // Busca server-side: usa versão "debounced" para evitar disparar a RPC a cada tecla
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  // Cache acumulado dos contatos já selecionados — necessário porque a lista
  // exibida vem paginada/buscada do servidor e os selecionados podem sair do array atual
  const [selectedContactsMap, setSelectedContactsMap] = useState<Record<string, Contact>>({});
  
  
  // Step 3 - Segmentação (nova opção)
  const [campaignSegment, setCampaignSegment] = useState<CampaignSegment>(createEmptySegment());
  const [segmentContacts, setSegmentContacts] = useState<SegmentPreviewContact[]>([]);
  
  // Step 4 - Mensagem (usando grupos de mensagens para diversificação)
  const [messageGroups, setMessageGroups] = useState<MessageGroup[]>([
    { id: crypto.randomUUID(), name: 'Grupo 1', items: [], weight: 100 }
  ]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateParams, setTemplateParams] = useState<TemplateParams>({});
  
  // Step 5 - Delay
  const [delayRange, setDelayRange] = useState([1000, 3000]);
  const [blockSize, setBlockSize] = useState(5);
  const [blockDelayRange, setBlockDelayRange] = useState([100, 400]);
  const [useContactName, setUseContactName] = useState(true);
  const [verifyWhatsapp, setVerifyWhatsapp] = useState(true);
  
  // Grupo de Blocos - nova funcionalidade para intervalos variáveis
  const [enableGroupBlock, setEnableGroupBlock] = useState(false);
  const [groupBlockIntervalRange, setGroupBlockIntervalRange] = useState([10, 20]); // Em minutos
  const [groupBlockIntervalUnit, setGroupBlockIntervalUnit] = useState<'minutes' | 'hours'>('minutes');
  const [groupBlockSize, setGroupBlockSize] = useState(20);
  
  // Step 6 - Ações pós-disparo
  const [campaignActions, setCampaignActions] = useState<CampaignActionItem[]>([]);
  
  // Step 7 - Agendamento
  const [scheduleType, setScheduleType] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState('09:00');
  
  // Modal de ajuda para WhatsApp Oficial
  const [showOficialHelp, setShowOficialHelp] = useState(false);

  // ---------------------------------------------------------------------------
  // Queries de Dados
  // ---------------------------------------------------------------------------
  
  // Busca inboxes ativas do tenant que o usuário TEM PERMISSÃO de acessar.
  // Reaproveita a RPC `get_user_accessible_inboxes` (mesma usada em iniciar
  // conversa, ChatArea etc.), garantindo que inboxes restritas (access_restricted)
  // só apareçam para agentes vinculados via inbox_agents.
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['campaign-inboxes', tenantId, user?.id, channelType],
    queryFn: async () => {
      const channelFilter: ChannelType[] = channelType === 'whatsapp_official'
        ? ['whatsapp_oficial']
        : ['whatsapp'];

      const { data, error } = await supabase.rpc('get_user_accessible_inboxes', {
        _user_id: user!.id,
        _tenant_id: tenantId!,
      });

      if (error) throw error;

      // Filtra por canal (regra atual) e status ativo. RPC já aplica permissão.
      const filtered = (data || []).filter((i: any) =>
        channelFilter.includes(i.channel_type as ChannelType) && i.status === 'active'
      );
      return filtered as InboxType[];
    },
    enabled: open && !!tenantId && !!user?.id,
  });

  // Debounce do termo de busca (300ms) — evita disparar RPC a cada tecla
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearch.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  // Busca contatos do tenant via RPC server-side (search_campaign_contacts).
  // - Sem busca: retorna apenas os 10 primeiros contatos (ordem alfabética).
  // - Com busca: filtra server-side em nome/telefone/email, sem limite de 1k do PostgREST.
  const {
    data: contactsResult,
    isLoading: loadingContacts,
    isFetching: fetchingContacts,
  } = useQuery({
    queryKey: ['campaign-contacts', tenantId, debouncedContactSearch],
    queryFn: async () => {
      campaignLogger.debug('Buscando contatos via RPC', { search: debouncedContactSearch });
      // Cast necessário até types.ts ser regenerado
      const { data, error } = await (supabase.rpc as any)('search_campaign_contacts', {
        p_search: debouncedContactSearch || null,
        p_limit: 10,
        p_offset: 0,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
      const rows = (data || []) as Array<Contact & { total_count: number }>;
      const total = rows[0]?.total_count ? Number(rows[0].total_count) : 0;
      return {
        contacts: rows.map(({ total_count: _t, ...c }) => c) as Contact[],
        totalCount: total,
      };
    },
    enabled: open && !!tenantId && recipientSource === 'contacts',
    placeholderData: (prev) => prev, // keepPreviousData v5 — UX suave ao digitar
  });

  const contacts: Contact[] = contactsResult?.contacts ?? [];
  const contactsTotalCount: number = contactsResult?.totalCount ?? 0;



  // Busca listas de campanhas
  // IMPORTANTE: usa o RPC get_campaign_lists (SECURITY DEFINER) em vez de
  // consulta direta na tabela — o RPC aplica a regra de visibilidade
  // (private | shared | by_profile | public) via user_can_see_entity,
  // mantendo o modal alinhado à tela "Importação de Listas > Contatos".
  const { data: campaignLists = [], isLoading: loadingLists } = useQuery({
    queryKey: ['campaign-lists-visible', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_campaign_lists', { p_tenant_hint: tenantId });

      if (error) throw error;
      return ((data || []) as any[])
        .filter((list) => list.status === 'ready')
        .map((list) => ({
          ...list,
          variable_columns: Array.isArray(list.variable_columns) ? list.variable_columns : [],
        }))
        .sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ) as CampaignList[];
    },
    enabled: open && !!tenantId,
  });

  // Busca itens da lista selecionada via RPC SECURITY DEFINER
  // Contorna problemas de RLS com cache de permissões em PgBouncer
  // Segue padrão estabelecido por get_campaign_lists (memória campaigns/lists-access-rpc-standard)
  const { data: listItems = [] } = useQuery({
    queryKey: ['list-items', selectedListId],
    queryFn: async () => {
      if (!selectedListId) return [];
      
      const { data, error } = await supabase
        .rpc('get_campaign_list_items', { p_list_id: selectedListId });
      
      if (error) throw error;
      return (data || []) as ListItem[];
    },
    enabled: !!selectedListId,
  });

  // Variáveis disponíveis da lista selecionada
  const availableListVariables = useMemo(() => {
    const selectedList = campaignLists.find(l => l.id === selectedListId);
    return selectedList?.variable_columns || [];
  }, [campaignLists, selectedListId]);

  // ---------------------------------------------------------------------------
  // Mutation para criar campanha
  // ---------------------------------------------------------------------------
  
  const createMutation = useMutation({
    mutationFn: async () => {
      // =========================================================================
      // Prepara dados para a função RPC atômica
      // =========================================================================
      
      // Monta dados da campanha baseado no tipo de canal
      let messageContent = '';
      let messageType: 'text' | 'template' = 'text';
      
      // Calcula total de itens de todos os grupos
      const totalItems = messageGroups.reduce((sum, g) => sum + g.items.length, 0);
      
      if (channelType === 'whatsapp_official' && selectedTemplate) {
        messageContent = `TEMPLATE:${selectedTemplate.name}`;
        messageType = 'template';
      } else if (totalItems > 0) {
        // Buscar primeiro item de texto do primeiro grupo para preview
        const firstGroupWithText = messageGroups.find(g => g.items.some(i => i.item_type === 'text'));
        const textItem = firstGroupWithText?.items.find(i => i.item_type === 'text');
        messageContent = textItem?.text_content || 'Mensagem da campanha';
      } else {
        messageContent = 'Mensagem da campanha';
      }

      // =========================================================================
      // Prepara recipients baseado na fonte
      // =========================================================================
      const recipientsData: Array<{
        phone: string;
        contact_name: string | null;
        contact_id: string | null;
        list_id: string | null;
        variables: Record<string, string> | null;
      }> = [];

      if (recipientSource === 'contacts') {
        // Usa o mapa acumulado (selectedContactsMap) ao invés da lista atual,
        // pois com busca server-side o contato selecionado pode não estar mais em `contacts`.
        const selectedContactsData = selectedContacts
          .map((id) => selectedContactsMap[id] ?? contacts.find((c) => c.id === id))
          .filter((c): c is Contact => !!c);
        selectedContactsData.forEach((contact) => {
          if (contact.phone) {
            recipientsData.push({
              phone: normalizeBrazilianPhone(contact.phone),
              contact_name: contact.name,
              contact_id: contact.id,
              list_id: null,
              variables: null,
            });
          }
        });
    } else if (recipientSource === 'list') {
        // Guarda defensivo: se listItems está vazio apesar de recipientCount > 0,
        // significa que a RPC falhou silenciosamente (ex: erro transitório de sessão)
        if (listItems.length === 0) {
          throw new Error('Não foi possível carregar os destinatários da lista. Recarregue a página e tente novamente.');
        }
        listItems.forEach((item) => {
          recipientsData.push({
            phone: normalizeBrazilianPhone(item.phone),
            contact_name: item.name,
            contact_id: null,
            list_id: selectedListId,
            variables: item.variables,
          });
        });
      } else if (recipientSource === 'manual') {
        manualRecipients.forEach((r) => {
          recipientsData.push({
            phone: normalizeBrazilianPhone(r.phone),
            contact_name: r.name || null,
            contact_id: null,
            list_id: null,
            variables: null,
          });
        });
      } else if (recipientSource === 'segment' && segmentContacts.length > 0) {
        // Segmentação - usar contatos do preview
        segmentContacts.forEach((contact) => {
          if (contact.phone) {
            recipientsData.push({
              phone: normalizeBrazilianPhone(contact.phone),
              contact_name: contact.name,
              contact_id: contact.id,
              list_id: null,
              variables: null,
            });
          }
        });
      }

      // =========================================================================
      // PROTEÇÃO LGPD: Filtrar recipients que estão na blacklist
      // =========================================================================
      let filteredRecipients = recipientsData;
      let blacklistedCount = 0;
      
      if (recipientsData.length > 0) {
        const phones = recipientsData.map(r => r.phone);
        const blacklistResult = await checkBlacklist(phones);
        
        // Filtrar recipients que não estão na blacklist
        filteredRecipients = recipientsData.filter(r => !blacklistResult.has(r.phone));
        blacklistedCount = recipientsData.length - filteredRecipients.length;
        
        if (blacklistedCount > 0) {
          campaignLogger.info(`[CreateCampaign] ${blacklistedCount} contatos da blacklist LGPD foram excluídos`);
          toast.info(`${blacklistedCount} contato(s) da blacklist LGPD foram excluídos da campanha`);
        }
      }

      // =========================================================================
      // Prepara message groups e items
      // =========================================================================
      // Tipos compatíveis com Json do Supabase (evita erro TS2322)
      type JsonValue = string | number | boolean | null | { [key: string]: JsonValue } | JsonValue[];
      
      const messageGroupsData: Array<{ name: string; weight: number }> = [];
      const messageItemsData: Array<{
        group_index: number;
        item_type: string;
        text_content: string | null;
        media_url: string | null;
        media_filename: string | null;
        media_mime_type: string | null;
        delay_seconds: number | null;
        buttons_config: JsonValue | null;
        contact_name: string | null;
        contact_phone: string | null;
        contact_business_description: string | null;
        template_name: string | null;
        template_language: string | null;
        template_params: JsonValue | null;
      }> = [];

      if (channelType !== 'whatsapp_official' && messageGroups.some(g => g.items.length > 0)) {
        messageGroups.forEach((group, groupIndex) => {
          messageGroupsData.push({
            name: group.name,
            weight: group.weight,
          });
          
          group.items.forEach((item) => {
            messageItemsData.push({
              group_index: groupIndex,
              item_type: item.item_type,
              text_content: item.text_content || null,
              media_url: item.media_url || null,
              media_filename: item.media_filename || null,
              media_mime_type: item.media_mime_type || null,
              delay_seconds: item.delay_seconds ?? null,
              buttons_config: (item.buttons_config || null) as unknown as JsonValue | null,
              contact_name: item.contact_name || null,
              contact_phone: item.contact_phone || null,
              contact_business_description: item.contact_business_description || null,
              template_name: null,
              template_language: null,
              template_params: null,
            });
          });
        });
      }

      // Para WhatsApp Oficial, adiciona item de template
      if (channelType === 'whatsapp_official' && selectedTemplate) {
        messageItemsData.push({
          group_index: -1, // Sem grupo
          item_type: 'template',
          text_content: null,
          media_url: null,
          media_filename: null,
          media_mime_type: null,
          delay_seconds: null,
          buttons_config: null,
          contact_name: null,
          contact_phone: null,
          contact_business_description: null,
          template_name: selectedTemplate.name,
          template_language: selectedTemplate.language,
          template_params: templateParams as unknown as JsonValue,
        });
      }

      // =========================================================================
      // Prepara ações pós-disparo
      // =========================================================================
      const actionsData = campaignActions.map((action) => ({
        action_type: action.action_type,
        action_value: action.action_value,
        execution_condition: action.execution_condition,
      }));

      // =========================================================================
      // Validação de payload antes da RPC (hardening)
      // PostgREST/supabase-js serializa automaticamente arrays JS para JSONB.
      // NÃO usar JSON.stringify() aqui, caso contrário o banco recebe uma string
      // escalar e jsonb_array_elements() falha com "cannot extract elements from a scalar".
      // =========================================================================
      if (!Array.isArray(filteredRecipients)) {
        campaignLogger.error('Payload inválido: filteredRecipients não é um array', filteredRecipients);
        throw new Error('Payload inválido: destinatários não é um array. Verifique origem dos dados.');
      }
      if (!Array.isArray(messageGroupsData)) {
        campaignLogger.error('Payload inválido: messageGroupsData não é um array', messageGroupsData);
        throw new Error('Payload inválido: grupos de mensagem não é um array.');
      }
      if (!Array.isArray(messageItemsData)) {
        campaignLogger.error('Payload inválido: messageItemsData não é um array', messageItemsData);
        throw new Error('Payload inválido: itens de mensagem não é um array.');
      }
      if (!Array.isArray(actionsData)) {
        campaignLogger.error('Payload inválido: actionsData não é um array', actionsData);
        throw new Error('Payload inválido: ações não é um array.');
      }

      // =========================================================================
      // Chama função RPC atômica - cria tudo em uma única transação
      // IMPORTANTE: Passar arrays diretamente (sem JSON.stringify)
      // =========================================================================
      const { data: campaignId, error: rpcError } = await supabase.rpc('create_campaign_atomic', {
        p_name: name,
        p_description: description || null,
        p_channel_type: channelType === 'whatsapp_official' ? 'whatsapp_oficial' : 'whatsapp',
        p_message_content: messageContent,
        p_message_type: messageType,
        p_media_url: null,
        p_media_caption: null,
        p_use_contact_name: useContactName,
        p_verify_whatsapp: verifyWhatsapp,
        p_delay_min_ms: delayRange[0],
        p_delay_max_ms: delayRange[1],
        p_block_size: blockSize,
        p_block_delay_min_ms: blockDelayRange[0],
        p_block_delay_max_ms: blockDelayRange[1],
        p_group_block_enabled: enableGroupBlock,
        p_group_block_size: groupBlockSize,
        p_group_block_interval_min_minutes: groupBlockIntervalUnit === 'hours' 
          ? groupBlockIntervalRange[0] * 60 
          : groupBlockIntervalRange[0],
        p_group_block_interval_max_minutes: groupBlockIntervalUnit === 'hours' 
          ? groupBlockIntervalRange[1] * 60 
          : groupBlockIntervalRange[1],
        p_inbox_ids: selectedInboxes,
        // Arrays passados diretamente - supabase-js serializa corretamente para JSONB
        p_recipients: filteredRecipients,
        p_message_groups: messageGroupsData,
        p_message_items: messageItemsData,
        p_actions: actionsData,
        // NOVO v1.1.0: Critérios de segmentação (apenas quando fonte = segment)
        p_segment_data: recipientSource === 'segment' ? campaignSegment as unknown as Json : null,
      });

      if (rpcError) {
        campaignLogger.error('[CreateCampaign] Erro na RPC create_campaign_atomic:', rpcError);
        throw new Error(rpcError.message);
      }

      // =========================================================================
      // Atualiza status e scheduled_at se necessário (pós-criação)
      // =========================================================================
      if (scheduleType === 'scheduled' && scheduledDate) {
        const [hours, minutes] = scheduledTime.split(':').map(Number);
        const dateWithTime = new Date(scheduledDate);
        dateWithTime.setHours(hours, minutes, 0, 0);
        
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({ 
            status: 'scheduled',
            scheduled_at: dateWithTime.toISOString(),
          })
          .eq('id', campaignId);
        
        if (updateError) {
          campaignLogger.warn('[CreateCampaign] Erro ao atualizar agendamento:', updateError);
        }
      }

      // Atualiza contagem total de recipients
      if (filteredRecipients.length > 0) {
        await supabase
          .from('campaigns')
          .update({ total_recipients: filteredRecipients.length })
          .eq('id', campaignId);
      }

      campaignLogger.info(`[CreateCampaign] Campanha criada com sucesso via RPC atômica: ${campaignId}`);
      return { id: campaignId };
    },
    onSuccess: () => {
      toast.success('Campanha criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      resetForm();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      campaignLogger.error('Erro ao criar campanha:', error);
      toast.error('Erro ao criar campanha: ' + error.message);
    },
  });

  // ---------------------------------------------------------------------------
  // Funções Auxiliares
  // ---------------------------------------------------------------------------
  
  // Reseta o formulário para estado inicial
  const resetForm = () => {
    setStep(1);
    setName('');
    setDescription('');
    setChannelType('whatsapp');
    setSelectedInboxes([]);
    setRecipientSource('list');
    setSelectedContacts([]);
    setSelectedContactsMap({});
    setSelectedListId(null);
    setManualRecipients([]);
    setManualPhone('');
    setManualName('');
    setContactSearch('');
    setDebouncedContactSearch('');
    setMessageGroups([{ id: crypto.randomUUID(), name: 'Grupo 1', items: [], weight: 100 }]);
    setSelectedTemplate(null);
    setTemplateParams({});
    setDelayRange([1000, 3000]);
    setBlockSize(5);
    setBlockDelayRange([100, 400]);
    setUseContactName(true);
    setVerifyWhatsapp(true);
    setEnableGroupBlock(false);
    setGroupBlockIntervalRange([10, 20]);
    setGroupBlockIntervalUnit('minutes');
    setGroupBlockSize(20);
    // Reset estados de segmentação (Step 3)
    setCampaignSegment(createEmptySegment());
    setSegmentContacts([]);
    setCampaignActions([]);
    setScheduleType('now');
    setScheduledDate(undefined);
    setScheduledTime('09:00');
  };

  // Toggle inbox selecionada
  const toggleInbox = (inboxId: string) => {
    setSelectedInboxes(prev =>
      prev.includes(inboxId)
        ? prev.filter(id => id !== inboxId)
        : [...prev, inboxId]
    );
  };

  // Toggle contato selecionado — também armazena/remove no mapa acumulado
  // para preservar os dados (nome/telefone) mesmo se a busca mudar.
  const toggleContact = (contact: Contact) => {
    setSelectedContacts((prev) =>
      prev.includes(contact.id)
        ? prev.filter((id) => id !== contact.id)
        : [...prev, contact.id],
    );
    setSelectedContactsMap((prev) => {
      if (prev[contact.id]) {
        // Desmarcar: remove do mapa
        const next = { ...prev };
        delete next[contact.id];
        return next;
      }
      // Marcar: adiciona/atualiza no mapa
      return { ...prev, [contact.id]: contact };
    });
  };

  // Seleciona/desmarca todos os contatos atualmente exibidos (lista server-side).
  const selectAllContacts = () => {
    const currentIds = contacts.map((c) => c.id);
    const allSelected = currentIds.length > 0 && currentIds.every((id) => selectedContacts.includes(id));
    if (allSelected) {
      // Desmarca os atualmente visíveis
      setSelectedContacts((prev) => prev.filter((id) => !currentIds.includes(id)));
      setSelectedContactsMap((prev) => {
        const next = { ...prev };
        currentIds.forEach((id) => delete next[id]);
        return next;
      });
    } else {
      // Marca todos os visíveis (mantém demais já marcados)
      setSelectedContacts((prev) => Array.from(new Set([...prev, ...currentIds])));
      setSelectedContactsMap((prev) => {
        const next = { ...prev };
        contacts.forEach((c) => {
          next[c.id] = c;
        });
        return next;
      });
    }
  };


  // Adiciona destinatário manual
  const addManualRecipient = () => {
    if (!manualPhone.trim()) return;
    
    const normalizedPhone = normalizeBrazilianPhone(manualPhone.trim());
    const existingPhones = createNormalizedPhoneSet(manualRecipients.map(r => r.phone));
    
    if (existingPhones.has(normalizedPhone)) {
      toast.error('Este telefone já foi adicionado');
      return;
    }
    
    setManualRecipients(prev => [...prev, { phone: normalizedPhone, name: manualName.trim() }]);
    setManualPhone('');
    setManualName('');
  };

  // Remove destinatário manual
  const removeManualRecipient = (index: number) => {
    setManualRecipients(prev => prev.filter((_, i) => i !== index));
  };

  // OBS: a busca passou a ser server-side (RPC search_campaign_contacts).
  // O array `contacts` já vem filtrado/paginado do backend, então não há mais
  // `filteredContacts` derivado no cliente.


  // Calcula contagem de destinatários - usa total_rows da lista para validação imediata
  const recipientCount = useMemo(() => {
    if (recipientSource === 'contacts') return selectedContacts.length;
    if (recipientSource === 'list') {
      // Usa total_rows da lista selecionada (disponível imediatamente)
      const selectedList = campaignLists.find(l => l.id === selectedListId);
      return selectedList?.total_rows || listItems.length;
    }
    if (recipientSource === 'manual') return manualRecipients.length;
    if (recipientSource === 'segment') return segmentContacts.length;
    return 0;
  }, [recipientSource, selectedContacts.length, selectedListId, campaignLists, listItems.length, manualRecipients.length, segmentContacts.length]);

  // Valida se o step atual está válido para avançar
  const isStepValid = (): boolean => {
    switch (step) {
      case 1:
        return name.trim().length >= 3;
      case 2:
        return selectedInboxes.length > 0;
      case 3: {
        // Trava de limite máximo de destinatários por campanha
        if (recipientCount > MAX_CAMPAIGN_RECIPIENTS) return false;
        // Para lista, verifica se há lista selecionada E se possui destinatários (v1.2.0)
        if (recipientSource === 'list') return !!selectedListId && recipientCount > 0;
        // Para segmento, verifica se há contatos no preview
        if (recipientSource === 'segment') return segmentContacts.length > 0;
        return recipientCount > 0;
      }
      case 4:
        if (channelType === 'whatsapp_official') {
          if (!selectedTemplate) return false;
          // Validar que todas as variáveis do body foram preenchidas
          const bodyComp = selectedTemplate.components?.find((c: any) => c.type === 'BODY');
          if (bodyComp?.text) {
            const varMatches = bodyComp.text.match(/\{\{\d+\}\}/g);
            const varCount = varMatches ? varMatches.length : 0;
            if (varCount > 0) {
              const bodyParams = templateParams.body_params || [];
              const allFilled = Array.from({ length: varCount }).every((_, i) =>
                bodyParams[i] && bodyParams[i].trim().length > 0
              );
              if (!allFilled) return false;
            }
          }
          return true;
        }
        return messageGroups.some(g => g.items.length > 0);
      case 5:
        return delayRange[0] <= delayRange[1] && blockDelayRange[0] <= blockDelayRange[1];
      case 6:
        // Step de ações é sempre válido (ações são opcionais)
        return true;
      case 7:
        if (scheduleType === 'scheduled') {
          return !!scheduledDate;
        }
        return true;
      default:
        return true;
    }
  };

  // Navega para próximo step
  const handleNext = () => {
    if (step < totalSteps && isStepValid()) {
      setStep(s => s + 1);
    }
  };

  // Navega para step anterior
  const handleBack = () => {
    if (step > 1) {
      setStep(s => s - 1);
    }
  };

  // Cria a campanha (com validação de limite de destinatários)
  const handleCreate = () => {
    // Validação v1.2.0: Impedir criação sem destinatários
    if (recipientCount === 0) {
      toast.error('Adicione pelo menos um destinatário à campanha.');
      return;
    }
    // Camada extra de segurança: validar limite de destinatários antes de criar
    if (recipientCount > MAX_CAMPAIGN_RECIPIENTS) {
      toast.error(`Limite máximo de ${MAX_CAMPAIGN_RECIPIENTS.toLocaleString('pt-BR')} contatos por campanha. Para realizar disparos maiores, entre em contato com o comercial UaizyChat.`);
      return;
    }
    if (isStepValid()) {
      createMutation.mutate();
    }
  };

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Pré-seleção de lista ao abrir (fluxo vindo da tela de Importação de Listas).
  // Mantém o comportamento padrão quando initialListId não é informado.
  useEffect(() => {
    if (open && initialListId) {
      setRecipientSource('list');
      setSelectedListId(initialListId);
    }
  }, [open, initialListId]);

  // Reset inboxes quando mudar tipo de canal
  useEffect(() => {
    setSelectedInboxes([]);
  }, [channelType]);

  // ---------------------------------------------------------------------------
  // Renderização do Conteúdo de cada Step
  // ---------------------------------------------------------------------------
  
  const renderStepContent = () => {
    switch (step) {
      // =========== STEP 1: CONFIGURAÇÃO ===========
      case 1:
        return (
          <div className="space-y-6">
            {/* Nome da Campanha */}
            <div className="space-y-2">
              <Label htmlFor="campaign-name" className="text-sm font-medium">
                Nome da Campanha *
              </Label>
              <Input
                id="campaign-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Black Friday 2024"
                className="h-11"
              />
              <p className="text-xs text-muted-foreground">
                Mínimo de 3 caracteres. Este nome é apenas para sua organização.
              </p>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="campaign-description" className="text-sm font-medium">
                Descrição (opcional)
              </Label>
              <Textarea
                id="campaign-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva o objetivo desta campanha..."
                rows={3}
              />
            </div>

            {/* Tipo de Canal */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Tipo de Canal *</Label>
              <RadioGroup
                value={channelType}
                onValueChange={(v) => setChannelType(v as 'whatsapp' | 'whatsapp_official')}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                <Label
                  htmlFor="channel-whatsapp"
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                    "hover:border-primary/50 hover:bg-accent/50",
                    channelType === 'whatsapp' 
                      ? "border-primary bg-primary/5" 
                      : "border-border"
                  )}
                >
                  <RadioGroupItem value="whatsapp" id="channel-whatsapp" className="sr-only" />
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium block">WhatsApp</span>
                    <span className="text-xs text-muted-foreground">Conexão via QR Code</span>
                  </div>
                  {channelType === 'whatsapp' && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </Label>

                <Label
                  htmlFor="channel-official"
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                    "hover:border-primary/50 hover:bg-accent/50",
                    channelType === 'whatsapp_official' 
                      ? "border-primary bg-primary/5" 
                      : "border-border"
                  )}
                >
                  <RadioGroupItem value="whatsapp_official" id="channel-official" className="sr-only" />
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium block">WhatsApp Oficial</span>
                    <span className="text-xs text-muted-foreground">API Business (Templates)</span>
                  </div>
                  {channelType === 'whatsapp_official' && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </Label>
              </RadioGroup>
            </div>
          </div>
        );

      // =========== STEP 2: INBOXES ===========
      case 2:
        return (
          <div className="space-y-4">
            {loadingInboxes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : inboxes.length === 0 ? (
              <div className="text-center py-12">
                <Inbox className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  Nenhuma caixa de entrada ativa encontrada para este tipo de canal.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Selecione uma ou mais caixas de entrada para enviar a campanha.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {inboxes.map((inbox) => (
                    <div
                      key={inbox.id}
                      onClick={() => toggleInbox(inbox.id)}
                      className={cn(
                        "p-4 rounded-lg border-2 cursor-pointer transition-all",
                        "hover:border-primary/50 hover:bg-accent/50",
                        selectedInboxes.includes(inbox.id)
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            inbox.status === 'active' ? "bg-green-500/10" : "bg-yellow-500/10"
                          )}>
                            <Inbox className={cn(
                              "h-5 w-5",
                              inbox.status === 'active' ? "text-green-500" : "text-yellow-500"
                            )} />
                          </div>
                          <div>
                            <p className="font-medium">{inbox.name}</p>
                            <p className="text-xs text-muted-foreground">{inbox.identifier}</p>
                          </div>
                        </div>
                        {selectedInboxes.includes(inbox.id) && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      // =========== STEP 3: DESTINATÁRIOS ===========
      case 3:
        return (
          <div className="space-y-4">
            {/* Alerta de limite máximo de destinatários excedido */}
            {recipientCount > MAX_CAMPAIGN_RECIPIENTS && (
              <div className="flex items-start gap-3 p-4 rounded-lg border border-destructive/50 bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive">
                    Limite máximo: {MAX_CAMPAIGN_RECIPIENTS.toLocaleString('pt-BR')} contatos por campanha
                  </p>
                  <p className="text-xs text-destructive/80 mt-1">
                    Você selecionou {recipientCount.toLocaleString('pt-BR')} contatos. Para realizar disparos maiores, entre em contato com o comercial UaizyChat.
                  </p>
                </div>
              </div>
            )}
            {/* Tabs de fonte de destinatários */}
            <Tabs value={recipientSource} onValueChange={(v) => setRecipientSource(v as 'contacts' | 'list' | 'manual' | 'segment')}>
              <TabsList className="grid w-full grid-cols-4">
                {/* Ordem: Lista CSV (default) → Contatos → Manual → Segmento */}
                <TabsTrigger value="list" className="gap-1 text-xs sm:text-sm">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Lista CSV</span>
                </TabsTrigger>
                <TabsTrigger value="contacts" className="gap-1 text-xs sm:text-sm">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Contatos</span>
                </TabsTrigger>
                <TabsTrigger value="manual" className="gap-1 text-xs sm:text-sm">
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Manual</span>
                </TabsTrigger>
                <TabsTrigger value="segment" className="gap-1 text-xs sm:text-sm">
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Segmento</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab: Contatos existentes */}
              <TabsContent value="contacts" className="mt-4 space-y-4">
                {/* Busca e ações — sempre visíveis (busca é server-side) */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Buscar contatos (nome, telefone ou e-mail)..."
                      className="pl-9"
                    />
                    {fetchingContacts && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllContacts}
                    disabled={contacts.length === 0}
                  >
                    {contacts.length > 0 && contacts.every((c) => selectedContacts.includes(c.id))
                      ? 'Desmarcar visíveis'
                      : 'Selecionar visíveis'}
                  </Button>
                </div>

                {/* Lista de contatos */}
                <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                  {loadingContacts ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-muted-foreground">
                        {debouncedContactSearch
                          ? 'Nenhum contato encontrado para a busca'
                          : 'Nenhum contato cadastrado'}
                      </p>
                    </div>
                  ) : (
                    contacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => toggleContact(contact)}
                        className={cn(
                          'flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 transition-colors',
                          'hover:bg-accent/50',
                          selectedContacts.includes(contact.id) && 'bg-primary/5',
                        )}
                      >
                        <Checkbox
                          checked={selectedContacts.includes(contact.id)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {contact.name || 'Sem nome'}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Resumo — mostra selecionados + total disponível no servidor */}
                <p className="text-sm text-muted-foreground">
                  {selectedContacts.length} contato(s) selecionado(s)
                  {contactsTotalCount > 0 && (
                    <>
                      {' '}— exibindo {contacts.length} de {contactsTotalCount.toLocaleString('pt-BR')}.
                      {contactsTotalCount > contacts.length && ' Use a busca para refinar.'}
                    </>
                  )}
                </p>
              </TabsContent>

              {/* Tab: Lista CSV */}
              <TabsContent value="list" className="mt-4 space-y-4">
                {loadingLists ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : campaignLists.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground mb-2">Nenhuma lista importada</p>
                    <p className="text-xs text-muted-foreground">
                      Vá para Listas no menu para importar um arquivo CSV
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {campaignLists.map((list) => {
                        // Indica se este card é o selecionado atualmente
                        const isSelected = selectedListId === list.id;
                        return (
                          <div
                            key={list.id}
                            onClick={() => setSelectedListId(list.id)}
                            className={cn(
                              "group relative flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all",
                              isSelected
                                ? "border-primary bg-primary/5 shadow-sm"
                                : "border-border/60 hover:border-border hover:shadow-md"
                            )}
                          >
                            {/* Ícone da lista (estilo "modern data card") */}
                            <div
                              className={cn(
                                "flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl transition-colors",
                                isSelected
                                  ? "bg-primary/10 text-primary"
                                  : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                              )}
                            >
                              <FileText className="h-5 w-5" />
                            </div>

                            {/* Conteúdo central: nome + meta-info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <h3
                                  className={cn(
                                    "text-sm font-semibold truncate transition-colors",
                                    isSelected ? "text-foreground" : "text-foreground/90"
                                  )}
                                >
                                  {list.name}
                                </h3>
                                {/* Ações do card: visibilidade da lista + qtde de variáveis */}
                                <div className="flex flex-shrink-0 items-center gap-1.5">
                                  {/* Badge de visibilidade (Todos/Compartilhada/Por perfil/Privada) */}
                                  <VisibilityBadge visibility={list.visibility ?? 'public'} />
                                  {list.variable_columns.length > 0 && (
                                    <span
                                      className={cn(
                                        "inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset",
                                        isSelected
                                          ? "bg-primary/10 text-primary ring-primary/20"
                                          : "bg-muted text-muted-foreground ring-border"
                                      )}
                                    >
                                      {list.variable_columns.length} variáveis
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5" />
                                  {list.total_rows} contatos
                                </div>
                                <span className="text-border">|</span>
                                <div className="flex items-center gap-1">
                                  <CalendarDays className="w-3.5 h-3.5" />
                                  {format(new Date(list.created_at), "dd/MM/yyyy", { locale: ptBR })}
                                </div>
                              </div>
                            </div>

                            {/* Indicador de seleção (radio-like) */}
                            <div className="flex-shrink-0 ml-2">
                              {isSelected ? (
                                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                  <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />
                                </div>
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2 border-border group-hover:border-primary/40 transition-colors" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {selectedListId && listItems.length > 0 && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-sm font-medium mb-2">
                          Preview ({listItems.length} destinatários):
                        </p>
                        <div className="text-xs text-muted-foreground space-y-1">
                          {listItems.slice(0, 3).map((item, idx) => (
                            <p key={idx}>
                              • {item.name || 'Sem nome'} - {item.phone}
                            </p>
                          ))}
                          {listItems.length > 3 && (
                            <p>... e mais {listItems.length - 3} contatos</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Tab: Manual */}
              <TabsContent value="manual" className="mt-4 space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <PhoneInput
                      value={manualPhone}
                      onChange={(value) => setManualPhone(value)}
                      placeholder="(62) 99999-9999"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="Nome (opcional)"
                    />
                  </div>
                  <Button onClick={addManualRecipient} disabled={!manualPhone.trim()}>
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>

                {manualRecipients.length > 0 && (
                  <div className="border rounded-lg max-h-[250px] overflow-y-auto">
                    {manualRecipients.map((recipient, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 border-b last:border-b-0"
                      >
                        <div>
                          <p className="font-medium">{recipient.name || 'Sem nome'}</p>
                          <p className="text-xs text-muted-foreground">{recipient.phone}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeManualRecipient(idx)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <p className="text-sm text-muted-foreground">
                  {manualRecipients.length} destinatários adicionados
                </p>
              </TabsContent>

              {/* Tab: Segmento - Filtros avançados */}
              <TabsContent value="segment" className="mt-4 space-y-4">
                <SegmentBuilder
                  segment={campaignSegment}
                  onSegmentChange={setCampaignSegment}
                  onContactsChange={setSegmentContacts}
                  crmEnabled={crmEnabled}
                />
              </TabsContent>
            </Tabs>
          </div>
        );

      // =========== STEP 4: MENSAGEM ===========
      case 4:
        return (
          <div className="space-y-4">
            {/* Botão de ajuda para WhatsApp Oficial */}
            {channelType === 'whatsapp_official' && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-sm text-muted-foreground">
                  Selecione um template aprovado e configure as variáveis.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="gap-2 border-primary/50 bg-primary/10 hover:bg-primary/20 text-primary shrink-0"
                  onClick={() => setShowOficialHelp(true)}
                >
                  <HelpCircle className="h-4 w-4" />
                  <span className="font-medium">Como Configurar?</span>
                  <Badge variant="default" className="ml-1 text-[10px] px-1.5 py-0">Novo</Badge>
                </Button>
              </div>
            )}
            
            {channelType === 'whatsapp_official' ? (
              <CampaignTemplateSelector
                selectedInboxes={selectedInboxes}
                selectedTemplate={selectedTemplate}
                onSelectTemplate={setSelectedTemplate}
                templateParams={templateParams}
                onTemplateParamsChange={setTemplateParams}
                availableListVariables={availableListVariables}
              />
            ) : (
              <MessageGroupManager
                groups={messageGroups}
                onGroupsChange={setMessageGroups}
                availableVariables={availableListVariables}
              />
            )}
          </div>
        );

      // =========== STEP 5: DELAY ===========
      case 5:
        return (
          <div className="space-y-6">
            {/* Delay entre mensagens - Slider */}
            <div className="space-y-3">
              <Label>Delay entre mensagens: {(delayRange[0] / 1000).toFixed(1)}s - {(delayRange[1] / 1000).toFixed(1)}s</Label>
              <Slider
                value={delayRange}
                onValueChange={setDelayRange}
                min={500}
                max={10000}
                step={100}
              />
            </div>
            
            {/* Tamanho do bloco - Slider */}
            <div className="space-y-3">
              <Label>Tamanho do bloco: {blockSize} mensagens</Label>
              <Slider
                value={[blockSize]}
                onValueChange={(v) => setBlockSize(v[0])}
                min={1}
                max={20}
                step={1}
              />
            </div>
            
            {/* Delay entre blocos - Slider */}
            <div className="space-y-3">
              <Label>Delay entre blocos: {(blockDelayRange[0] / 1000).toFixed(1)}s - {(blockDelayRange[1] / 1000).toFixed(1)}s</Label>
              <Slider
                value={blockDelayRange}
                onValueChange={setBlockDelayRange}
                min={50}
                max={2000}
                step={50}
              />
            </div>

            <Separator />

            {/* Grupo de Blocos - Nova funcionalidade */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Grupo de Blocos</Label>
                  <p className="text-xs text-muted-foreground">
                    Agrupa envios com intervalos variáveis para evitar detecção
                  </p>
                </div>
                <Switch checked={enableGroupBlock} onCheckedChange={setEnableGroupBlock} />
              </div>

              {enableGroupBlock && (
                <div className="space-y-4 p-4 rounded-lg bg-muted/50 border">
                  {/* Intervalo entre grupos - Range slider */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>
                        Intervalo entre grupos: {groupBlockIntervalRange[0]} - {groupBlockIntervalRange[1]} {groupBlockIntervalUnit === 'hours' ? 'h' : 'min'}
                      </Label>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant={groupBlockIntervalUnit === 'minutes' ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            if (groupBlockIntervalUnit !== 'minutes') {
                              // Convertendo de horas para minutos
                              setGroupBlockIntervalRange([
                                groupBlockIntervalRange[0] * 60,
                                groupBlockIntervalRange[1] * 60
                              ]);
                              setGroupBlockIntervalUnit('minutes');
                            }
                          }}
                        >
                          Min
                        </Button>
                        <Button
                          type="button"
                          variant={groupBlockIntervalUnit === 'hours' ? 'default' : 'outline'}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            if (groupBlockIntervalUnit !== 'hours') {
                              // Convertendo de minutos para horas
                              setGroupBlockIntervalRange([
                                Math.max(1, Math.round(groupBlockIntervalRange[0] / 60)),
                                Math.max(1, Math.round(groupBlockIntervalRange[1] / 60))
                              ]);
                              setGroupBlockIntervalUnit('hours');
                            }
                          }}
                        >
                          Horas
                        </Button>
                      </div>
                    </div>
                    <Slider
                      value={groupBlockIntervalRange}
                      onValueChange={setGroupBlockIntervalRange}
                      min={groupBlockIntervalUnit === 'hours' ? 1 : 5}
                      max={groupBlockIntervalUnit === 'hours' ? 24 : 120}
                      step={groupBlockIntervalUnit === 'hours' ? 1 : 5}
                    />
                    <p className="text-xs text-muted-foreground">
                      Sistema escolherá aleatoriamente um intervalo entre esses valores
                    </p>
                  </div>

                  {/* Mensagens por grupo */}
                  <div className="space-y-3">
                    <Label>Mensagens por grupo: {groupBlockSize}</Label>
                    <Slider
                      value={[groupBlockSize]}
                      onValueChange={(v) => setGroupBlockSize(v[0])}
                      min={5}
                      max={200}
                      step={5}
                    />
                    <p className="text-xs text-muted-foreground">
                      Total de mensagens enviadas antes do intervalo do grupo
                    </p>
                  </div>

                  {/* Resumo visual */}
                  <div className="p-3 rounded-md bg-background border text-xs space-y-1">
                    <p className="font-medium text-muted-foreground">Resumo:</p>
                    <p>
                      A cada <span className="font-semibold text-primary">{groupBlockSize}</span> mensagens, 
                      o sistema aguardará entre{' '}
                      <span className="font-semibold text-primary">
                        {groupBlockIntervalRange[0]}-{groupBlockIntervalRange[1]} {groupBlockIntervalUnit === 'hours' ? 'horas' : 'minutos'}
                      </span>{' '}
                      antes de enviar o próximo grupo.
                    </p>
                    <p className="text-muted-foreground">
                      Blocos de {blockSize} msgs com delay de {(delayRange[0]/1000).toFixed(1)}-{(delayRange[1]/1000).toFixed(1)}s entre msgs
                      {' '}e {(blockDelayRange[0]/1000).toFixed(1)}-{(blockDelayRange[1]/1000).toFixed(1)}s entre blocos
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator />
            
            {/* Usar nome do contato */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Usar nome do contato</Label>
                <p className="text-xs text-muted-foreground">Substitui {'{{nome}}'} pelo nome</p>
              </div>
              <Switch checked={useContactName} onCheckedChange={setUseContactName} />
            </div>
            
            {/* Verificar WhatsApp */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Verificar WhatsApp</Label>
                <p className="text-xs text-muted-foreground">Verifica se número tem WhatsApp</p>
              </div>
              <Switch checked={verifyWhatsapp} onCheckedChange={setVerifyWhatsapp} />
            </div>
          </div>
        );

      // =========== STEP 6: AÇÕES PÓS-DISPARO ===========
      case 6:
        return (
          <CampaignActionBuilder
            items={campaignActions}
            onItemsChange={setCampaignActions}
          />
        );

      // =========== STEP 7: AGENDAMENTO ===========
      case 7:
        return (
          <div className="space-y-6">
            {/* Opções de agendamento */}
            <RadioGroup
              value={scheduleType}
              onValueChange={(v) => setScheduleType(v as 'now' | 'scheduled')}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <Label
                htmlFor="schedule-now"
                className={cn(
                  "flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
                  "hover:border-primary/50 hover:bg-accent/50",
                  scheduleType === 'now' ? "border-primary bg-primary/5" : "border-border"
                )}
              >
                <RadioGroupItem value="now" id="schedule-now" className="sr-only" />
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Zap className="h-6 w-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <span className="font-medium block">Enviar Agora</span>
                  <span className="text-sm text-muted-foreground">
                    Iniciar envio imediatamente
                  </span>
                </div>
                {scheduleType === 'now' && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </Label>

              <Label
                htmlFor="schedule-later"
                className={cn(
                  "flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
                  "hover:border-primary/50 hover:bg-accent/50",
                  scheduleType === 'scheduled' ? "border-primary bg-primary/5" : "border-border"
                )}
              >
                <RadioGroupItem value="scheduled" id="schedule-later" className="sr-only" />
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <CalendarDays className="h-6 w-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <span className="font-medium block">Agendar</span>
                  <span className="text-sm text-muted-foreground">
                    Escolher data e hora
                  </span>
                </div>
                {scheduleType === 'scheduled' && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </Label>
            </RadioGroup>

            {/* Seleção de data e hora */}
            {scheduleType === 'scheduled' && (
              <div className="p-4 border rounded-lg space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !scheduledDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {scheduledDate
                            ? format(scheduledDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                            : "Selecione uma data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={scheduledDate}
                          onSelect={setScheduledDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Hora</Label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Resumo final */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <h3 className="font-medium flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-primary" />
                Resumo da Campanha
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Nome</p>
                  <p className="font-medium">{name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Canal</p>
                  <p className="font-medium">
                    {channelType === 'whatsapp_official' ? 'WhatsApp Oficial' : 'WhatsApp'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Caixas de entrada</p>
                  <p className="font-medium">{selectedInboxes.length}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Destinatários</p>
                  <p className="font-medium">{recipientCount}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Envio</p>
                  <p className="font-medium">
                    {scheduleType === 'now'
                      ? 'Imediato'
                      : scheduledDate
                        ? format(scheduledDate, "dd/MM/yyyy", { locale: ptBR }) + ' às ' + scheduledTime
                        : 'Pendente'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Renderização Principal
  // ---------------------------------------------------------------------------
  
  const currentStep = STEPS[step - 1];
  const StepIcon = currentStep.icon;

  return (
  <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0 flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          {/* ============================================================ */}
          {/* SIDEBAR - Navegação de Steps + Resumo */}
          {/* ============================================================ */}
          <aside 
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Header da Sidebar */}
            <div className="relative z-10 p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Megaphone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Nova Campanha</h2>
                  <p className="text-xs text-white/60">
                    {step} de {totalSteps} etapas
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de Steps */}
            <nav className="relative z-10 flex-1 p-4 space-y-1 overflow-y-auto">
              {STEPS.map((s) => {
                const isActive = step === s.id;
                const isCompleted = step > s.id;
                const Icon = s.icon;

                return (
                  <button
                    key={s.id}
                    onClick={() => {
                      // Permite voltar para steps anteriores ou ir para o atual
                      if (s.id <= step) setStep(s.id);
                    }}
                    disabled={s.id > step}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all",
                      "hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed",
                      isActive && "bg-white/10 border border-white/20",
                      !isActive && !isCompleted && "text-white/50"
                    )}
                  >
                    {/* Círculo indicador */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      "border-2 transition-colors",
                      isCompleted && "bg-green-500 border-green-500 text-white",
                      isActive && "bg-white border-white text-primary",
                      !isActive && !isCompleted && "border-white/30 text-white/50"
                    )}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>

                    {/* Texto do step */}
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        "font-medium text-sm block truncate",
                        isActive && "text-white",
                        isCompleted && "text-white/70",
                        !isActive && !isCompleted && "text-white/50"
                      )}>
                        {s.label}
                      </span>
                    </div>

                    {/* Badge de contagem (quando aplicável) */}
                    {isCompleted && s.id === 2 && selectedInboxes.length > 0 && (
                      <Badge className="text-xs bg-white/20 text-white border-0 hover:bg-white/20">
                        {selectedInboxes.length}
                      </Badge>
                    )}
                    {isCompleted && s.id === 3 && recipientCount > 0 && (
                      <Badge className="text-xs bg-white/20 text-white border-0 hover:bg-white/20">
                        {recipientCount}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Resumo em tempo real */}
            <div className="relative z-10 p-4 border-t border-white/10 bg-white/5">
              <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                Resumo
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Inboxes</span>
                  <span className="font-medium text-white">{selectedInboxes.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Destinatários</span>
                  <span className="font-medium text-white">{recipientCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Mensagens</span>
                  <span className="font-medium text-white">
                    {channelType === 'whatsapp_official' 
                      ? (selectedTemplate ? '1 template' : '0') 
                      : `${messageGroups.reduce((sum, g) => sum + g.items.length, 0)} (${messageGroups.length} ${messageGroups.length === 1 ? 'grupo' : 'grupos'})`}
                  </span>
                </div>
              </div>
            </div>
          </aside>

          {/* ============================================================ */}
          {/* CONTEÚDO PRINCIPAL */}
          {/* ============================================================ */}
          <main className="flex flex-col h-full min-h-0">
            {/* Header do Step */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStep.label}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStep.description}
                  </DialogDescription>
                </div>
                {/* Badge mobile mostrando progresso */}
                <Badge variant="outline" className="md:hidden">
                  {step}/{totalSteps}
                </Badge>
              </div>

              {/* Stepper mobile */}
              <div className="flex items-center gap-1.5 mt-4 md:hidden">
                {STEPS.map((s) => (
                  <div
                    key={s.id}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors",
                      step > s.id && "bg-green-500",
                      step === s.id && "bg-primary",
                      step < s.id && "bg-muted"
                    )}
                  />
                ))}
              </div>
            </DialogHeader>

            {/* Área de Conteúdo Scrollável */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="px-6 py-5">
                {renderStepContent()}
              </div>
            </div>

            {/* Footer com Navegação - sempre visível */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-background flex items-center justify-between sticky bottom-0">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={step === 1}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </Button>

              <span className="text-sm text-muted-foreground hidden sm:block">
                Etapa {step} de {totalSteps}
              </span>

              {step < totalSteps ? (
                <Button
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="gap-1"
                >
                  Próximo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleCreate}
                  disabled={!isStepValid() || createMutation.isPending}
                  className="gap-2"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Criar Campanha
                    </>
                  )}
                </Button>
              )}
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>

    {/* Modal de ajuda para campanhas WhatsApp Oficial */}
    <CampaignOficialHelpDialog 
      open={showOficialHelp} 
      onOpenChange={setShowOficialHelp} 
    />
  </>
  );
}
